import React, { useState } from 'react';
import { useCompanyStore } from '../../../stores/companyStore';
import { notify } from '../../../stores/notificationStore';
import { confirm } from '../../../stores/confirmStore';
import {
  isVatExempt,
  isIntraCommunity,
  resolveDocumentVatExemptArticle,
  formatDate,
} from '../../../utils/vatCalculations';
import { InvoiceTemplateProps, InvoiceItem } from '../types';
import { ActionBar } from '../shared/ActionBar';
import { CompanyInfoSection } from '../shared/CompanyInfoSection';
import { ClientInfoSection } from '../shared/ClientInfoSection';
import { ItemsTable } from '../shared/ItemsTable';
import { TotalsSection } from '../shared/TotalsSection';
import { LegalFooter } from '../shared/LegalFooter';
import { UnifiedEmailModal, EmailData } from '../../modals/UnifiedEmailModal';
import { PaymentSchedulesManagement } from '../../../features/payments';
import { publicLinksAPI, invoicesAPI } from '../../../utils/api';
import { logger } from '../../../utils/logger';

/**
 * Template Elegant (BUSINESS)
 * - Logo: top-center (centré)
 * - Header: classic (élégant, lignes dorées)
 * - Table: bordered (bordures dorées)
 * - Couleur par défaut: Or (#D4AF37) / Gris foncé (#1F2937)
 * - Design: Luxueux, prestige, professionnel haut de gamme
 */
export const InvoiceTemplateElegant: React.FC<InvoiceTemplateProps> = ({
  invoiceData,
  onClose,
  template,
  company: companyProp,
  color: colorProp
}) => {
  const { company: companyStore, invoiceColor: invoiceColorStore } = useCompanyStore();
  const [isLoading, setIsLoading] = useState(false);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [showPaymentSchedules, setShowPaymentSchedules] = useState(false);
  const [publicLink, setPublicLink] = useState<string>('');

  const company = companyProp || companyStore;
  const invoiceColor = colorProp || invoiceColorStore || template?.primaryColor || '#D4AF37';
  const secondaryColor = template?.secondaryColor || '#1F2937';

  const clientCountryCode = (invoiceData as any)?.Client?.countryCode;
  const isExempt = isVatExempt(company?.vatRegime) || isIntraCommunity(clientCountryCode, parseFloat(String(invoiceData?.totalTVA)) || undefined);
  const vatExemptArticle = resolveDocumentVatExemptArticle(company?.vatRegime, clientCountryCode, parseFloat(String(invoiceData?.totalTVA)) || undefined);

  if (!invoiceData) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-xl shadow-lg p-6 max-w-md text-center border-2" style={{ borderColor: invoiceColor }}>
          <p className="text-red-600 font-semibold mb-4">Erreur : Aucune donnée de facture</p>
          <button onClick={onClose} className="px-4 py-2 text-white rounded-lg transition-all" style={{ backgroundColor: invoiceColor }}>
            Retour
          </button>
        </div>
      </div>
    );
  }

  const items: InvoiceItem[] = typeof invoiceData.items === 'string'
    ? JSON.parse(invoiceData.items).map((item: any) => {
        const unitPrice = Number(item.unitPrice) || 0;
        const quantity = Number(item.quantity) || 0;
        return {
          ...item,
          unitPrice,
          quantity,
          total: Number(item.totalHT) || Number(item.total) || (unitPrice * quantity),  // ✅ Fallback vers calcul
          description: item.description || '',
          vatRate: item.taxPercent || (isExempt ? 0 : 20),  // ✅ Prisma utilise taxPercent
          vatAmount: item.vatAmount || 0,
        };
      })
    : (invoiceData.items || []).map((item: any) => {
        const unitPrice = Number(item.unitPrice) || 0;
        const quantity = Number(item.quantity) || 0;
        return {
          ...item,
          unitPrice,
          quantity,
          total: Number(item.totalHT) || Number(item.total) || (unitPrice * quantity),  // ✅ Fallback vers calcul
          description: item.description || '',
          vatRate: item.taxPercent || (isExempt ? 0 : 20),  // ✅ Prisma utilise taxPercent
          vatAmount: item.vatAmount || 0,
        };
      });

  const discountType = invoiceData.discountType || 'none';
  const discountValue = parseFloat(String(invoiceData.discountValue)) || 0;
  const discountAmount = parseFloat(String(invoiceData.discountAmount)) || 0;

  // ✅ Construire la ventilation TVA à partir des items (affichage uniquement)
  const vatBreakdown: Array<{ rate: number; baseHT: number; vatAmount: number }> = [];
  const vatByRate: Record<number, { baseHT: number; vatAmount: number }> = {};

  items.forEach((item: InvoiceItem) => {
    const rate = item.vatRate || 0;
    if (!vatByRate[rate]) {
      vatByRate[rate] = { baseHT: 0, vatAmount: 0 };
    }
    vatByRate[rate].baseHT += item.total || 0;
    vatByRate[rate].vatAmount += item.vatAmount || 0;
  });

  Object.entries(vatByRate).forEach(([rate, data]) => {
    vatBreakdown.push({
      rate: Number(rate),
      baseHT: data.baseHT,
      vatAmount: data.vatAmount
    });
  });

  // ✅ Utiliser les valeurs du backend (source de vérité)
  const subtotal = parseFloat(String(invoiceData.totalHT)) || parseFloat(String(invoiceData.subtotal)) || 0;
  const tax = parseFloat(String(invoiceData.totalTVA)) || parseFloat(String(invoiceData.tax)) || 0;
  const total = parseFloat(String(invoiceData.totalTTC)) || parseFloat(String(invoiceData.total)) || 0;

  const handleGeneratePDF = async () => {
    if (!invoiceData.id) {
      notify.error('ID de la facture manquant', 'Génération PDF');
      return;
    }
    setIsLoading(true);
    try {
      // ✅ Utilisation du backend pour générer le PDF (templates uniformisés)
      const blob = await invoicesAPI.downloadPDF(invoiceData.id);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const clientName = (invoiceData as any).Client?.fullName || (invoiceData as any).Client?.companyName || 'Client';
      a.download = `Facture_${invoiceData.invoiceNumber}_${clientName.replace(/\s+/g, '_')}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      notify.success('PDF généré avec succès', 'Génération PDF');
    } catch (error) {
      logger.error('Erreur génération PDF:', error);
      notify.error("Erreur lors de la génération du PDF : " + (error as Error).message, 'Génération PDF');
    } finally {
      setIsLoading(false);
    }
  };

  const handleGenerateFacturX = async () => {
    if (!invoiceData.id) {
      notify.error('ID de la facture manquant', 'Factur-X');
      return;
    }
    setIsLoading(true);
    try {
      // ✅ Utilisation du backend pour générer le PDF Factur-X (templates uniformisés)
      const blob = await invoicesAPI.downloadFacturXPDF(invoiceData.id);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const clientName = (invoiceData as any).Client?.fullName || (invoiceData as any).Client?.companyName || 'Client';
      a.download = `Facture_FacturX_${invoiceData.invoiceNumber}_${clientName.replace(/\s+/g, '_')}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      notify.success('PDF Factur-X généré avec succès', 'Factur-X');
    } catch (error) {
      logger.error('Erreur Factur-X:', error);
      notify.error("Erreur lors de la génération du PDF Factur-X : " + (error as Error).message, 'Erreur Factur-X');
    } finally {
      setIsLoading(false);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  const handleOpenEmailModal = async () => {
    try {
      setIsLoading(true);
      // ✅ Utilisation de l'API Layer
      if (!invoiceData.id) {
        throw new Error('ID de la facture manquant');
      }
      const result = await publicLinksAPI.getInvoiceLink(invoiceData.id);
      if (result.success && result.data && result.data.length > 0) {
        const activeLink = result.data.find((link: any) => link.isActive);
        if (activeLink) {
          setPublicLink(activeLink.url);
        }
      }
    } catch (error) {
      logger.error('Erreur lors de la récupération du lien public:', error);
    } finally {
      setIsLoading(false);
      setShowEmailModal(true);
    }
  };

  const handleSendEmail = async (emailData: EmailData) => {
    try {
      if (!invoiceData?.id) {
        throw new Error('ID de la facture manquant');
      }

      // Générer le PDF Factur-X de la facture
      setIsLoading(true);
      const { generateFacturXPDF, convertInvoiceToFacturX } = await import('../../../utils/facturx');
      const facturxData = convertInvoiceToFacturX({ ...invoiceData, items: items }, company);
      const pdfBlob = await generateFacturXPDF(facturxData);

      // Préparer FormData pour l'envoi
      const formData = new FormData();
      formData.append('to', emailData.to);
      formData.append('subject', emailData.subject);
      formData.append('body', emailData.body);
      formData.append('pdf', pdfBlob, `Facture_${invoiceData.invoiceNumber}.pdf`);

      // ✅ Utilisation de l'API Layer
      await publicLinksAPI.sendInvoiceEmail(invoiceData.id, formData);

      notify.success(`Email envoyé à ${emailData.to}`, 'Envoi réussi');
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Erreur inconnue';
      notify.error(message, 'Erreur d\'envoi');
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen print:min-h-0 print:bg-white">
      <ActionBar
        onGeneratePDF={handleGeneratePDF}
        onGenerateFacturX={handleGenerateFacturX}
        onSendEmail={handleOpenEmailModal}
        onPrint={handlePrint}
        onClose={onClose}
        onPaymentSchedules={() => setShowPaymentSchedules(true)}
        isLoading={isLoading}
        documentType="invoice"
        invoiceStatus={invoiceData.status}
      />

      {/* Payment Schedules Modal */}
      {showPaymentSchedules && invoiceData.id && (
        <PaymentSchedulesManagement
          invoiceId={invoiceData.id}
          invoiceNumber={invoiceData.invoiceNumber}
          invoiceTotal={total}
          onClose={() => setShowPaymentSchedules(false)}
        />
      )}

      <UnifiedEmailModal
        isOpen={showEmailModal}
        onClose={() => setShowEmailModal(false)}
        onSend={handleSendEmail}
        documentType="invoice"
        documentNumber={invoiceData.invoiceNumber}
        clientEmail={invoiceData.clientEmail || ''}
        clientName={invoiceData.clientName || ''}
        showPdfPreview={false}
        templateVariables={{
          invoice_number: invoiceData.invoiceNumber || '',
          client_name: invoiceData.clientName || '',
          total: total.toFixed(2),
          public_link: publicLink,
          due_date: formatDate(invoiceData.dueDate || invoiceData.issueDate),
          payment_conditions: company?.defaultPaymentTermsDays ? `Paiement à ${company.defaultPaymentTermsDays} jours` : 'Paiement à 30 jours',
          iban: company?.iban || '',
          bic: company?.bic || '',
          companyName: company?.legalName || company?.tradeName || '',
          companyEmail: company?.email || '',
          companyPhone: company?.phone || '',
        }}
      />

      <div className="py-8 print:py-0 print:mt-0">
        <div className="max-w-4xl mx-auto bg-white shadow-2xl print:shadow-none print:max-w-full border-2 print:border-0" style={{ borderColor: invoiceColor }} id="invoice-template">
          <div className="p-4 md:p-8">
            {/* Header Elegant - Logo CENTRÉ avec lignes décoratives */}
            <div className="text-center mb-8 border-b-2 pb-6" style={{ borderColor: invoiceColor }}>
              {/* Company Info (Logo CENTER) */}
              <CompanyInfoSection
                company={company}
                companyName={company?.legalName || company?.tradeName}
                companyAddress={company?.addressLine1}
                companyCity={company?.city}
                companyPostalCode={company?.zipCode}
                companyPhone={company?.phone}
                companyEmail={company?.email}
                logoPosition="top-center"
              />
            </div>

            {/* FACTURE Badge - Elegant style (doré, centré) */}
            <div className="text-center mb-8">
              <div className="inline-block">
                <div className="border-4 px-6 py-3 rounded-sm" style={{ borderColor: invoiceColor }}>
                  <h3 className="text-xs font-bold uppercase tracking-widest" style={{ color: secondaryColor }}>Facture</h3>
                  <span className="text-xl md:text-2xl font-bold" style={{ color: invoiceColor }}>
                    {invoiceData.invoiceNumber}
                  </span>
                </div>
              </div>
            </div>

            {/* Invoice Info Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
              {/* Left: Dates */}
              <div className="border-l-4 pl-4" style={{ borderColor: invoiceColor }}>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="font-semibold" style={{ color: secondaryColor }}>Date d'émission :</span>
                    <span className="text-gray-700">{formatDate(invoiceData.issueDate)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="font-semibold" style={{ color: secondaryColor }}>Date d'échéance :</span>
                    <span className="text-gray-700">{formatDate(invoiceData.dueDate)}</span>
                  </div>
                </div>
              </div>

              {/* Right: Client */}
              <div className="border-l-4 pl-4" style={{ borderColor: invoiceColor }}>
                <ClientInfoSection data={invoiceData} accentColor={invoiceColor} />
              </div>
            </div>

            {/* Reference Information - Elegant style */}
            {(invoiceData.purchaseOrderNumber || invoiceData.reference || invoiceData.projectName) && (
              <div className="mb-6 border-2 rounded-lg p-4" style={{ borderColor: invoiceColor }}>
                <h3 className="text-sm font-bold uppercase tracking-widest mb-3" style={{ color: invoiceColor }}>Références</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm text-gray-700">
                  {invoiceData.purchaseOrderNumber && (
                    <div>
                      <span className="font-semibold" style={{ color: secondaryColor }}>Bon de Commande :</span>
                      <p className="mt-1">{invoiceData.purchaseOrderNumber}</p>
                    </div>
                  )}
                  {invoiceData.reference && (
                    <div>
                      <span className="font-semibold" style={{ color: secondaryColor }}>Référence :</span>
                      <p className="mt-1">{invoiceData.reference}</p>
                    </div>
                  )}
                  {invoiceData.projectName && (
                    <div>
                      <span className="font-semibold" style={{ color: secondaryColor }}>Projet :</span>
                      <p className="mt-1">{invoiceData.projectName}</p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Titre du document */}
            {invoiceData.title && (
              <div
                className="mb-6 text-2xl font-semibold border-l-4 pl-4 text-gray-800"
                style={{ borderColor: invoiceColor }}
              >
                {invoiceData.title}
              </div>
            )}

            {/* Items Table - Style BORDERED (avec couleur dorée) */}
            <ItemsTable
              items={items}
              tableStyle="bordered"
              accentColor={invoiceColor}
              showVat={!isExempt}
            />

            {/* Totals Section */}
            <TotalsSection
              subtotal={subtotal}
              tax={tax}
              total={total}
              discount={discountAmount}
              discountType={discountType}
              discountValue={discountValue}
              vatBreakdown={vatBreakdown}
              accentColor={invoiceColor}
              showVatBreakdown={!isExempt}
              isVatExempt={isExempt}
              vatExemptionMention={vatExemptArticle}
            />

            {/* Notes Internes (affichées sur le document) */}
            {invoiceData.internalNotes && (
              <div className="mt-8">
                <h3 className="text-lg font-semibold mb-4 pb-2 border-b-2" style={{ color: secondaryColor, borderColor: invoiceColor }}>
                  Notes
                </h3>
                <p className="text-gray-700 p-4 rounded-lg text-sm whitespace-pre-wrap border" style={{ backgroundColor: invoiceColor + '15', borderColor: invoiceColor + '40' }}>
                  {invoiceData.internalNotes}
                </p>
              </div>
            )}

            {/* Mentions particulières */}
            {invoiceData.termsConditions && (
              <div className="mt-8">
                <h3 className="text-lg font-semibold mb-4 pb-2 border-b-2" style={{ color: secondaryColor, borderColor: invoiceColor }}>
                  Mentions particulières
                </h3>
                <p className="text-gray-700 p-4 rounded-lg text-sm whitespace-pre-wrap border" style={{ backgroundColor: invoiceColor + '15', borderColor: invoiceColor + '40' }}>
                  {invoiceData.termsConditions}
                </p>
              </div>
            )}

            {/* Legal Footer */}
            <LegalFooter company={company} notes={invoiceData.notes} vatExemptArticle={vatExemptArticle} />
          </div>
        </div>
      </div>
    </div>
  );
};
